import type { WheelSegment } from './types.js';

/** Preset palette offered in the segment editor. */
export const SEGMENT_COLORS = [
  '#8b5cf6',
  '#db2777',
  '#38bdf8',
  '#fbbf24',
  '#34d399',
  '#f43f5e',
  '#a78bfa',
  '#221d3a',
] as const;

/**
 * Seed segments for the free daily wheel: mostly small credits, rare jackpots.
 * Shipped with the display odds telling the truth — diverging them is a
 * deliberate act the owner takes, not a default we hand them.
 */
export const INITIAL_DAILY_SEGMENTS: WheelSegment[] = [
  {
    id: 'd1',
    name: '25 credits',
    reward: 'credits',
    value: 25,
    realChance: 44,
    displayChance: 44,
    color: '#38bdf8',
    icon: 'coins',
    active: true,
  },
  {
    id: 'd2',
    name: 'Try again',
    reward: 'nothing',
    value: 0,
    realChance: 30,
    displayChance: 30,
    color: '#221d3a',
    icon: 'ban',
    active: true,
  },
  {
    id: 'd3',
    name: '50 credits',
    reward: 'credits',
    value: 50,
    realChance: 18,
    displayChance: 18,
    color: '#8b5cf6',
    icon: 'coins',
    active: true,
  },
  {
    id: 'd4',
    name: '150 credits',
    reward: 'credits',
    value: 150,
    realChance: 6,
    displayChance: 6,
    color: '#34d399',
    icon: 'star',
    active: true,
  },
  {
    id: 'd5',
    name: '500 credits',
    reward: 'credits',
    value: 500,
    realChance: 2,
    displayChance: 2,
    color: '#fbbf24',
    icon: 'crown',
    active: true,
  },
];

/** Seed segments the page starts from. Replaced by real config later. */
export const INITIAL_SEGMENTS: WheelSegment[] = [
  {
    id: 's1',
    name: '100 credits',
    reward: 'credits',
    value: 100,
    realChance: 30,
    displayChance: 30,
    color: '#8b5cf6',
    icon: 'coins',
    active: true,
  },
  {
    id: 's2',
    name: 'Try again',
    reward: 'nothing',
    value: 0,
    realChance: 24,
    displayChance: 24,
    color: '#221d3a',
    icon: 'ban',
    active: true,
  },
  {
    id: 's3',
    name: '250 credits',
    reward: 'credits',
    value: 250,
    realChance: 18,
    displayChance: 18,
    color: '#db2777',
    icon: 'star',
    active: true,
  },
  {
    id: 's4',
    name: '50 credits',
    reward: 'credits',
    value: 50,
    realChance: 15,
    displayChance: 15,
    color: '#38bdf8',
    icon: 'coins',
    active: true,
  },
  {
    id: 's5',
    name: '500 credits',
    reward: 'credits',
    value: 500,
    realChance: 9,
    displayChance: 9,
    color: '#fbbf24',
    icon: 'crown',
    active: true,
  },
  {
    id: 's6',
    name: 'Exclusive role',
    reward: 'role',
    value: 0,
    realChance: 4,
    displayChance: 4,
    color: '#34d399',
    icon: 'shield',
    active: true,
  },
];

/**
 * Seed prizes for the scratch card.
 *
 * Built so the card is a **sink**, not a faucet: at the default price of 20 credits
 * the average credit win is under the price, and the rest of the value is coupons.
 * A card that paid out more than it cost would be a free source of credits, which
 * nothing in this economy is allowed to be. Like the wheels, the odds shown are the
 * truth until the owner chooses otherwise, and every credit prize is a `bonus` that
 * never raises a rank.
 */
export const INITIAL_SCRATCH_SEGMENTS: WheelSegment[] = [
  {
    id: 'k1',
    name: 'Not this time',
    reward: 'nothing',
    value: 0,
    realChance: 36,
    displayChance: 36,
    color: '#221d3a',
    icon: 'ban',
    active: true,
  },
  {
    id: 'k2',
    name: '10 credits',
    reward: 'credits',
    value: 10,
    realChance: 28,
    displayChance: 28,
    color: '#38bdf8',
    icon: 'coins',
    active: true,
  },
  {
    id: 'k3',
    name: '20 credits',
    reward: 'credits',
    value: 20,
    realChance: 16,
    displayChance: 16,
    color: '#8b5cf6',
    icon: 'coins',
    active: true,
  },
  {
    id: 'k4',
    name: '5% coupon',
    reward: 'coupon',
    value: 5,
    realChance: 8,
    displayChance: 8,
    color: '#db2777',
    icon: 'ticket',
    active: true,
  },
  {
    id: 'k5',
    name: '25 credits',
    reward: 'credits',
    value: 25,
    realChance: 7,
    displayChance: 7,
    color: '#34d399',
    icon: 'coins',
    active: true,
  },
  {
    id: 'k6',
    name: '100 credits',
    reward: 'credits',
    value: 100,
    realChance: 4,
    displayChance: 4,
    color: '#fbbf24',
    icon: 'star',
    active: true,
  },
  {
    id: 'k7',
    name: 'Golden card — 500 credits',
    reward: 'credits',
    value: 500,
    realChance: 1,
    displayChance: 1,
    color: '#fbbf24',
    icon: 'crown',
    active: true,
  },
];
